import { AppBar, Badge, Box, Container, Link, Toolbar, Typography } from '@mui/material';
import { BaseTheme, IconButton, stickyTopBarProps } from 'design-system';
import { useFormDialog } from 'providers/FormDialogProvider';
import { IconType } from 'react-icons';
import { MdChat, MdDatasetLinked } from 'react-icons/md';
import { Link as RouterLink, useLocation } from 'react-router';
import { getPrefixedRoute, getRoutePrefix, getThemeVariant, ThemeVariant } from 'services/theming';
import MoreInfoTooltip from 'components/MoreInfoTooltip';
import { isStaging, isLocal, isProd } from 'config/env';
import { useBuyersMarketCart } from 'hooks/useBuyersMarketCart';
import useIsDarkMode from 'hooks/useIsDarkMode';
import useNavigationItems from 'hooks/useNavigationItems';
import useOnlineStatus, { OnlineStatus } from 'hooks/useOnlineStatus';
import useUser from 'hooks/useUser';
import Logo from 'images/LogoColor';
import LogoFreshmark from 'images/LogoFreshmark';
import LogoPlain from 'images/LogoPlain';
import LogoVilla from 'images/LogoVilla';
import { ButtonColor, FormType } from 'generated/graphql';
import CartHeaderBadge from './CartHeaderBadge';
import CreditBalance from './CreditBalance';
import ProfileConfigButton from './ProfileConfig/ProfileConfigButton';
import ProfileSwitch, { ProfileSwitchSize } from './ProfileSwitch';
import { useWhiteLabelling } from './WhiteLabellingProvider';

const noticeStyle = (theme: BaseTheme) => ({
  marginLeft: theme.spacing(1),
  color: theme.palette.warning.main,
  fontSize: '1.25rem',
  fontWeight: theme.typography.fontWeightBold,
});

const chatButtonStyle = (theme: BaseTheme) => ({
  color: theme.palette.common.white,
  backgroundColor: theme.palette.secondary.light,
  padding: theme.spacing(0.75),
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
});

const HeaderLogo = () => {
  const isDarkMode = useIsDarkMode();
  const themeVariant = getThemeVariant();
  return (
    <>
      {[ThemeVariant.Nile, ThemeVariant.Inputs].includes(themeVariant) && (
        <Box sx={{ maxWidth: 60, minWidth: 60 }}>{isDarkMode ? <LogoPlain /> : <Logo />}</Box>
      )}
      {themeVariant === ThemeVariant.FreshMark && (
        <Box sx={(theme) => ({ maxWidth: 120, minWidth: 120, marginBottom: theme.spacing(1) })}>
          <LogoFreshmark />
        </Box>
      )}
      {themeVariant === ThemeVariant.Villa && (
        <Box sx={() => ({ maxWidth: 100, minWidth: 100 })}>
          <LogoVilla width={100} height={50} />
        </Box>
      )}
    </>
  );
};

const OnlineStatusToolTip = () => {
  const status = useOnlineStatus();
  if (status !== OnlineStatus.Offline) {
    return null;
  }

  return (
    <MoreInfoTooltip
      sx={{ paddingY: 1, paddingLeft: 1 }}
      color="warning"
      description={
        <Typography>
          {`You're currently offline. While you can still access your stored data, please note that certain features are
          limited until you regain a network connection.`}
        </Typography>
      }
    />
  );
};

const HeaderNotice = () => {
  return (
    <>
      {isProd && isLocal && <Box sx={noticeStyle}>🚧 Production API 🚧</Box>}
      {isStaging && <Box sx={noticeStyle}>🚧 Staging API 🚧</Box>}
      {isLocal && !isStaging && !isProd && <Box sx={noticeStyle}>🏗️ Local API🏗️</Box>}
    </>
  );
};

const HeaderBadge = ({ Icon }: { Icon: IconType }) => {
  return (
    <Badge
      badgeContent={0}
      sx={(theme) => ({
        marginLeft: 0,
        '& > svg': {
          fontSize: '1.6em',
          margin: theme.spacing(-0.25, 0, 0, 0.5),
        },
        '& > span': {
          height: 16,
          transform: 'scale(1) translate(65%, -70%)',
          background: theme.palette.info.main,
        },
      })}
    >
      <Icon size={16} />
    </Badge>
  );
};

const HeaderProviderBadge = ({ Icon }: { Icon: IconType }) => {
  const { cartGroupCases } = useBuyersMarketCart();

  return (
    <Badge
      badgeContent={cartGroupCases.toString()}
      sx={(theme) => ({
        marginLeft: theme.spacing(-0.5),
        '& > svg': {
          fontSize: '1.6em',
          margin: theme.spacing(-0.25, 2, 0, 0),
        },
        '& > span': {
          height: 16,
          transform: 'scale(1) translate(30%, -10%)',
          background: theme.palette.success.light,
        },
      })}
    >
      <Icon size={16} />
    </Badge>
  );
};

export enum HeaderLinkVariant {
  Cart = 'Cart',
}

const getHeaderLinkContent = (label: string, Icon: IconType) => {
  switch (label) {
    case HeaderLinkVariant.Cart:
      return <CartHeaderBadge Icon={Icon} />;
    default:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          <Icon size={16} />
        </Box>
      );
  }
};

const Header = () => {
  const { openFormDialog } = useFormDialog();
  const isDarkMode = useIsDarkMode();
  const { isFreshmarkProfile } = useWhiteLabelling();
  const location = useLocation();
  const headerLinks = useNavigationItems();
  const { user } = useUser();
  const prefix = getRoutePrefix();

  const setSeedFormDialog = () => {
    openFormDialog({
      open: true,
      editIDs: [],
      eTags: [],
      type: FormType.SeededPresets,
      addButton: false,
    });
  };

  return (
    <AppBar
      enableColorOnDark
      {...stickyTopBarProps}
      sx={(theme) => ({ [theme.breakpoints.down('md')]: { display: 'none' } })}
    >
      <Toolbar
        sx={(theme) => ({
          background: isDarkMode ? theme.palette.primary.main : theme.palette.background.paper,
          borderBottom: `solid 1px ${theme.palette.divider}`,
          padding: 0,
          minHeight: '48px',
          [theme.breakpoints.up('sm')]: {
            minHeight: '48px',
          },
        })}
      >
        <Container
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: 0,
            maxWidth: '100% !important',
          }}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
            })}
          >
            <HeaderLogo />
            <OnlineStatusToolTip />
            <HeaderNotice />
            {(isLocal || isStaging) && (
              <IconButton
                sx={(theme) => ({
                  color: isDarkMode ? theme.palette.background.paper : theme.palette.primary.main,
                })}
                onClick={setSeedFormDialog}
              >
                <MdDatasetLinked size={20} />
              </IconButton>
            )}
            <Box sx={{ marginLeft: 2 }}>
              <ProfileConfigButton />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {headerLinks.map((link) => {
              const { label, path, icon: Icon } = link;

              const isCreditBalance = label === 'Credit Balance';
              const isCart = label === 'Cart';
              const isRequestQuote = label === 'Request';

              const isRoundedColorBadge = isCart || isRequestQuote; // Badge containing rounded coloured background. E.g., carts badge

              const adjustedPrefix = prefix.length === 0 ? '/' : prefix;
              const isActivePath =
                (path.includes(location.pathname) && location.pathname !== adjustedPrefix) ||
                (path.includes(location.search) && location.search.length > 0);

              return isCreditBalance ? (
                <CreditBalance key={'credit-balance'} color={isDarkMode ? undefined : ButtonColor.Primary} />
              ) : (
                <Link
                  key={path}
                  component={RouterLink}
                  to={path}
                  sx={(theme) => ({
                    backgroundColor: isRoundedColorBadge
                      ? theme.palette.tertiary.main
                      : isDarkMode
                        ? theme.palette.primary.main
                        : theme.palette.common.white,
                    padding: isRoundedColorBadge ? theme.spacing(0.5, 2) : theme.spacing(0.5, 1.5),
                    borderRadius: isRoundedColorBadge ? theme.spacing(10) : theme.spacing(1),
                    color:
                      isActivePath && !isDarkMode
                        ? theme.palette.secondary.main
                        : isDarkMode && !isRoundedColorBadge
                          ? theme.palette.common.white
                          : theme.palette.primary.main,
                    '&:hover': {
                      color: theme.palette.primary.light,
                    },
                    opacity: !isActivePath && !isRoundedColorBadge && isDarkMode ? 0.5 : undefined,
                    fontSize: '0.98rem',
                    fontWeight: theme.typography.fontWeightBold,
                  })}
                >
                  {getHeaderLinkContent(label, Icon)}
                </Link>
              );
            })}

            {user && <ProfileSwitch size={ProfileSwitchSize.Small} isButton />}
            {isFreshmarkProfile ? (
              <IconButton
                as={RouterLink}
                to={getPrefixedRoute('/chat')}
                sx={{ chatButtonStyle }}
                size="small"
                aria-label="Chat"
              >
                <MdChat size={20} />
              </IconButton>
            ) : null}
          </Box>
        </Container>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
